import { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { ArrowLeftIcon, ArrowRightIcon, ForwardIcon, PencilIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { LOG_PRODUCTION_BATCH, LOG_TRANSACTION, SET_DAILY_TRANSACTION_TOTAL } from '../../../graphql/production-mutations'
import { GET_DAILY_LEDGER_RECIPES } from '../../../graphql/production-queries'
import toast from 'react-hot-toast'
import { toBatch, batchToWeight, weightToBatch } from '../utils'

export interface RecipeLedgerRow {
  raw_material_id: number
  raw_material_name: string
  unit: string
  output_quantity: number | null
  cooked_quantity: number
  reheated_quantity: number
  stored_quantity: number
  sold_quantity: number
  sold_portions: number
  sold_portions_breakdown: { product_name: string; variant_name: string; portions: number }[]
  sold_portions_by_options: { option_item_ids: number[]; portions: number; option_labels: string }[]
  expired_quantity: number
  wasted_quantity: number
  consumed_staff_quantity: number
  variance_quantity: number
  current_stock: number
}

export { toBatch }

type TransactionType =
  | 'reheated'
  | 'stored'
  | 'expired'
  | 'wasted'
  | 'consumed_staff'
  | 'sold_catering'
  | 'sold_other'

type ReasonValue = 'spoilage' | 'overproduction' | 'quality' | 'other'

export interface RecipeLogStepProps {
  title: string
  description: string
  hint?: string
  recipes: RecipeLedgerRow[]
  date: string
  quantityKey: keyof RecipeLedgerRow
  logType: 'cooked' | TransactionType
  showReason?: boolean
  /** Pre-fill the input for each recipe. Key = raw_material_id, value = quantity (weight). */
  prefillValues?: Record<number, number>
  /** If set, adds a column to the table showing the prefill value with this header. */
  prefillColumnLabel?: string
  canLog: boolean
  onLogged: () => void
  onNext: () => void
  onBack: () => void
  isFirst: boolean
}

const REASONS: { value: ReasonValue; label: string }[] = [
  { value: 'spoilage', label: 'Spoilage' },
  { value: 'overproduction', label: 'Overproduction' },
  { value: 'quality', label: 'Quality issue' },
  { value: 'other', label: 'Other' },
]

interface RowState {
  qty: string       // weight field (base unit) — always what gets sent to API
  batchQty: string  // batch field — derived from qty or typed directly
  reason: ReasonValue | ''
  saving: boolean
  editing: boolean
}

const defaultRow = (): RowState => ({ qty: '', batchQty: '', reason: '', saving: false, editing: false })

const RecipeLogStep = ({
  title,
  description,
  hint,
  recipes,
  date,
  quantityKey,
  logType,
  showReason = false,
  prefillValues,
  prefillColumnLabel,
  canLog,
  onLogged,
  onNext,
  onBack,
  isFirst,
}: RecipeLogStepProps) => {
  const [rows, setRows] = useState<Record<number, RowState>>({})

  // When prefill values arrive (async from parent query), initialise rows that
  // haven't been touched yet — also compute the batch field if output_quantity is available.
  useEffect(() => {
    if (!prefillValues) return
    setRows((prev) => {
      const next = { ...prev }
      for (const [rawId, qty] of Object.entries(prefillValues)) {
        const id = Number(rawId)
        if (!next[id] || next[id].qty === '') {
          const recipe = recipes.find((r) => r.raw_material_id === id)
          const batch = toBatch(qty, recipe?.output_quantity)
          next[id] = { ...defaultRow(), qty: String(qty), batchQty: batch !== null ? String(batch) : '' }
        }
      }
      return next
    })
  }, [prefillValues, recipes])

  const setRow = (id: number, patch: Partial<RowState>) =>
    setRows((prev) => {
      const current = prev[id] ?? defaultRow()
      return { ...prev, [id]: { ...current, ...patch } }
    })

  const onWeightChange = (id: number, value: string, outputQty: number | null | undefined) => {
    const w = parseFloat(value)
    const batch = !isNaN(w) && outputQty && outputQty > 0 ? String(weightToBatch(w, outputQty)) : ''
    setRow(id, { qty: value, batchQty: batch })
  }

  const onBatchChange = (id: number, value: string, outputQty: number | null | undefined) => {
    const b = parseFloat(value)
    const weight = !isNaN(b) && outputQty && outputQty > 0 ? String(batchToWeight(b, outputQty)) : ''
    setRow(id, { batchQty: value, qty: weight })
  }

  const [logBatch] = useMutation(LOG_PRODUCTION_BATCH, {
    refetchQueries: [{ query: GET_DAILY_LEDGER_RECIPES, variables: { date } }],
    onError: (e) => toast.error(e.message),
  })

  const [logTx] = useMutation(LOG_TRANSACTION, {
    refetchQueries: [{ query: GET_DAILY_LEDGER_RECIPES, variables: { date } }],
    onError: (e) => toast.error(e.message),
  })

  const [setTotal] = useMutation(SET_DAILY_TRANSACTION_TOTAL, {
    refetchQueries: [{ query: GET_DAILY_LEDGER_RECIPES, variables: { date } }],
    onError: (e) => toast.error(e.message),
  })

  const handleAdd = async (recipe: RecipeLedgerRow) => {
    const row = rows[recipe.raw_material_id] ?? defaultRow()
    const qty = parseFloat(row.qty)
    if (isNaN(qty) || qty <= 0) { toast.error('Enter a positive quantity'); return }

    setRow(recipe.raw_material_id, { saving: true })
    try {
      if (logType === 'cooked') {
        await logBatch({
          variables: { data: { raw_material_id: recipe.raw_material_id, ledger_date: date, quantity: qty } },
        })
      } else {
        await logTx({
          variables: {
            data: {
              raw_material_id: recipe.raw_material_id,
              transaction_type: logType,
              ledger_date: date,
              quantity: qty,
              reason: showReason && row.reason ? row.reason : undefined,
            },
          },
        })
      }
      setRow(recipe.raw_material_id, { qty: '', batchQty: '', reason: '', saving: false })
      toast.success(`Logged for ${recipe.raw_material_name}`)
      onLogged()
    } catch {
      setRow(recipe.raw_material_id, { saving: false })
    }
  }

  const handleUpdateFromEdit = async (recipe: RecipeLedgerRow) => {
    const row = rows[recipe.raw_material_id] ?? defaultRow()
    const qty = parseFloat(row.qty)
    if (isNaN(qty) || qty < 0) { toast.error('Enter a valid quantity'); return }
    await handleUpdate(recipe, qty)
  }

  const handleUpdate = async (recipe: RecipeLedgerRow, quantity: number) => {
    setRow(recipe.raw_material_id, { saving: true })
    try {
      await setTotal({
        variables: {
          data: {
            raw_material_id: recipe.raw_material_id,
            ledger_date: date,
            transaction_type: logType,
            quantity,
          },
        },
      })
      setRow(recipe.raw_material_id, { qty: '', batchQty: '', saving: false, editing: false })
      toast.success(quantity === 0 ? `Cleared ${recipe.raw_material_name}` : `Updated ${recipe.raw_material_name}`)
      onLogged()
    } catch {
      setRow(recipe.raw_material_id, { saving: false })
    }
  }

  return (
    <div className="space-y-6">
      {/* Step header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
        {hint && <p className="mt-1 text-xs text-gray-400 italic">{hint}</p>}
      </div>

      {/* Recipe table */}
      {recipes.length === 0 ? (
        <p className="text-sm text-gray-400">No tracked recipes found.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipe</th>
                {prefillColumnLabel && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-amber-600 uppercase tracking-wider">{prefillColumnLabel}</th>
                )}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Already logged today</th>
                {canLog && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Add entry</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {recipes.map((recipe) => {
                const alreadyLogged = recipe[quantityKey] as number
                const row = rows[recipe.raw_material_id] ?? defaultRow()
                const storedQty = prefillValues?.[recipe.raw_material_id]
                const hasPrefill = storedQty != null && storedQty > 0
                const batchLogged = toBatch(alreadyLogged, recipe.output_quantity)
                const batchStored = storedQty != null ? toBatch(storedQty, recipe.output_quantity) : null

                return (
                  <tr key={recipe.raw_material_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{recipe.raw_material_name}</span>
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        {recipe.unit}
                      </span>
                    </td>

                    {/* Prefill column (e.g. "Stored last night") */}
                    {prefillColumnLabel && (
                      <td className="px-4 py-3 text-right">
                        {hasPrefill ? (
                          <>
                            <span className="block font-semibold text-amber-700">{storedQty} {recipe.unit}</span>
                            {batchStored !== null && (
                              <span className="block text-xs text-amber-500">{batchStored} batch</span>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    )}

                    {/* Already logged today */}
                    <td className="px-4 py-3 text-right">
                      {alreadyLogged > 0 ? (
                        <span className="inline-flex items-center gap-2">
                          <span>
                            <span className="block font-medium text-gray-900">{alreadyLogged} {recipe.unit}</span>
                            {batchLogged !== null && (
                              <span className="block text-xs text-gray-400">{batchLogged} batch</span>
                            )}
                          </span>
                          {canLog && !row.editing && (
                            <button
                              onClick={() => setRow(recipe.raw_material_id, {
                                editing: true,
                                qty: String(alreadyLogged),
                                batchQty: recipe.output_quantity
                                  ? String(weightToBatch(alreadyLogged, recipe.output_quantity))
                                  : '',
                              })}
                              className="text-gray-400 hover:text-primary-600 shrink-0"
                              title="Correct this value"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>

                    {/* Input cell */}
                    {canLog && (
                      <td className="px-4 py-3">
                        {hasPrefill && alreadyLogged > 0 && !row.editing ? (
                          /* Prefill row is confirmed — input hidden, pencil in "already logged" handles corrections */
                          <span className="inline-flex items-center gap-1.5 text-xs text-green-700">
                            <CheckCircleIcon className="h-4 w-4" />
                            Confirmed
                          </span>
                        ) : row.editing ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Weight input */}
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step="any"
                                min="0"
                                value={row.qty}
                                onChange={(e) => onWeightChange(recipe.raw_material_id, e.target.value, recipe.output_quantity)}
                                onKeyDown={(e) => e.key === 'Enter' && handleUpdateFromEdit(recipe)}
                                placeholder={`${recipe.unit}`}
                                className="input w-24 text-sm py-1.5 border-amber-300 bg-amber-50"
                                disabled={row.saving}
                                autoFocus
                              />
                              <span className="text-xs text-gray-500">{recipe.unit}</span>
                            </div>
                            {/* Batch input — only when output_quantity is set */}
                            {recipe.output_quantity != null && recipe.output_quantity > 0 && (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  value={row.batchQty}
                                  onChange={(e) => onBatchChange(recipe.raw_material_id, e.target.value, recipe.output_quantity)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateFromEdit(recipe)}
                                  placeholder="batch"
                                  className="input w-20 text-sm py-1.5 border-amber-300 bg-amber-50"
                                  disabled={row.saving}
                                />
                                <span className="text-xs text-gray-500">batch</span>
                              </div>
                            )}
                            <button
                              onClick={() => handleUpdateFromEdit(recipe)}
                              disabled={row.saving || row.qty === ''}
                              className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-400 disabled:opacity-40"
                            >
                              {row.saving ? '…' : 'Update'}
                            </button>
                            <button
                              onClick={() => handleUpdate(recipe, 0)}
                              disabled={row.saving}
                              className="rounded-md border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40"
                            >
                              Clear
                            </button>
                            <button
                              onClick={() => setRow(recipe.raw_material_id, { editing: false, qty: '', batchQty: '' })}
                              disabled={row.saving}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Weight input */}
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step="any"
                                min="0"
                                value={row.qty}
                                onChange={(e) => onWeightChange(recipe.raw_material_id, e.target.value, recipe.output_quantity)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAdd(recipe)}
                                placeholder={`${recipe.unit}`}
                                className={`input w-24 text-sm py-1.5 ${hasPrefill && row.qty ? 'border-amber-300 bg-amber-50' : ''}`}
                                disabled={row.saving}
                              />
                              <span className="text-xs text-gray-500">{recipe.unit}</span>
                            </div>
                            {/* Batch input — only when output_quantity is set */}
                            {recipe.output_quantity != null && recipe.output_quantity > 0 && (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  value={row.batchQty}
                                  onChange={(e) => onBatchChange(recipe.raw_material_id, e.target.value, recipe.output_quantity)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleAdd(recipe)}
                                  placeholder="batch"
                                  className={`input w-20 text-sm py-1.5 ${hasPrefill && row.batchQty ? 'border-amber-300 bg-amber-50' : ''}`}
                                  disabled={row.saving}
                                />
                                <span className="text-xs text-gray-500">batch</span>
                              </div>
                            )}
                            {showReason && (
                              <select
                                value={row.reason}
                                onChange={(e) => setRow(recipe.raw_material_id, { reason: e.target.value as ReasonValue })}
                                className="input text-sm py-1.5"
                                disabled={row.saving}
                              >
                                <option value="">Reason…</option>
                                {REASONS.map((r) => (
                                  <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                              </select>
                            )}
                            <button
                              onClick={() => handleAdd(recipe)}
                              disabled={row.saving || !row.qty}
                              className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-500 disabled:opacity-40"
                            >
                              {row.saving ? '…' : hasPrefill && row.qty ? 'Confirm' : 'Add'}
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <button
          onClick={onBack}
          disabled={isFirst}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={onNext}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
          >
            <ForwardIcon className="h-4 w-4" />
            Nothing to log
          </button>
          <button
            onClick={onNext}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500"
          >
            Next
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default RecipeLogStep
